import * as React from 'react';
import type { MetadataResponse, NewShippingRate } from '~/services/api.server';

export function useInitShippingInfo(metadata?: MetadataResponse['results']) {
  React.useEffect(() => {
    if (metadata) {
      initShippingInfo(metadata);
    }
  }, [metadata]);
}

async function initShippingInfo(metadata: MetadataResponse['results']) {
  try {
    localStorage.setItem('shipping-info-state', 'loading');
    const shippingZonesVersion = metadata.find((m) => m.key === 'SHIPPING_ZONES_VERSION')?.value ?? '0';
    const usRatesVersion = metadata.find((m) => m.key === 'SHIPPING_US_RATES_VERSION')?.value ?? '0';
    const internationalRatesVersion =
      metadata.find((m) => m.key === 'SHIPPING_INTERNATIONAL_RATES_VERSION')?.value ?? '0';

    const _shippingZonesVersion = localStorage.getItem('shipping-zones-version');
    const _usRatesVersion = localStorage.getItem('us-rates-version');
    const _internationalRatesVersion = localStorage.getItem('international-rates-version');

    let toUpdate: Record<string, Function> = {};
    if (_shippingZonesVersion !== shippingZonesVersion) {
      toUpdate['shipping-zones'] = () =>
        fetch('/api/shipping-zones?version=' + shippingZonesVersion).then((res) => res.json());
    }
    const shouldUpdateUsRates = _usRatesVersion !== usRatesVersion;
    if (shouldUpdateUsRates) {
      toUpdate['us-rates'] = () => fetch('/api/us-rates?version=' + usRatesVersion).then((res) => res.json());
    }
    const shouldUpdateInternationalRates = _internationalRatesVersion !== internationalRatesVersion;
    if (shouldUpdateInternationalRates) {
      toUpdate['international-rates'] = () =>
        fetch('/api/international-rates?version=' + internationalRatesVersion).then((res) => res.json());
    }

    if (Object.keys(toUpdate).length === 0) {
      localStorage.setItem('shipping-info-state', 'idle');
      return;
    }

    const results = await Promise.all(Object.values(toUpdate).map((fn) => fn()));

    if (shouldUpdateUsRates || shouldUpdateInternationalRates) {
      Object.keys(localStorage).forEach((key) => {
        if (
          typeof key === 'string' &&
          ((shouldUpdateUsRates && key.startsWith('us-rate-')) ||
            (shouldUpdateInternationalRates && key.startsWith('international-rate-')))
        ) {
          localStorage.removeItem(key);
        }
      });
    }

    Object.keys(toUpdate).forEach((key, i) => {
      if (key === 'shipping-zones') {
        localStorage.setItem('shipping-zones', JSON.stringify(results[i]));
        localStorage.setItem('shipping-zones-version', shippingZonesVersion);
      } else if (key === 'us-rates') {
        const usRates = results[i] as Record<string, NewShippingRate[]>;
        Object.keys(usRates).forEach((key) => {
          localStorage.setItem('us-rate-' + key, JSON.stringify(usRates[key]));
        });
        localStorage.setItem('us-rates-version', usRatesVersion);
      } else if (key === 'international-rates') {
        const internationalRates = results[i] as Record<string, NewShippingRate[]>;
        Object.keys(internationalRates).forEach((key) => {
          localStorage.setItem('international-rate-' + key, JSON.stringify(internationalRates[key]));
        });
        localStorage.setItem('international-rates-version', internationalRatesVersion);
      }
    });

    localStorage.setItem('shipping-info-state', 'idle');
  } catch (error) {
    localStorage.setItem('shipping-info-state', 'error');
  }
}
